import { ContentfulCollection } from 'contentful';
import { ApiClient } from '../client/ApiClient';
import { ApiResponse } from '../types';
import { ContentfulCampaign, ContentfulCollection as JbContentfulCollection } from './types';
import resolveResponse from 'contentful-resolve-response';
import { CollectionQuery } from '../contentfulThemeApi';
import { BasicEntryCollection } from '../contentfulThemeApi/ContentfulThemeApiClient';
import { AttachCategorySuggestionsQuery, EntriesQuery } from '../contentfulThemeApi/types';
import { gql } from 'graphql-tag';

export const CONTENTFUL_PREVIEW_HOST = 'preview.contentful.com';
export const CONTENTFUL_PUBLISHED_HOST = 'cdn.contentful.com';

export class ContentfulClient extends ApiClient {
  private readonly _accessToken: string;
  private readonly _environment: string;
  private readonly _space: string;
  private readonly _host: string;

  constructor({
    accessToken,
    environment,
    space,
    host = CONTENTFUL_PUBLISHED_HOST,
  }: {
    accessToken?: string;
    environment?: string;
    space?: string;
    host?: string;
  }) {
    super();
    if (!accessToken || !environment || !space) {
      throw new Error('ContentfulClient: accessToken, environment, and space are required');
    }
    this._accessToken = accessToken;
    this._space = space;
    this._environment = environment;
    this._host = host;
  }

  protected get requestHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this._accessToken}`,
    };
  }

  getEntries = async <T>(
    queryOptions: Record<string, string>,
    config?: EntriesQuery['config']
  ): Promise<ApiResponse<BasicEntryCollection<T>>> => {
    const { data, statusCode, error } = await this.getUnresolvedEntries<T>(queryOptions);
    return this.getResolvedData<T>(error, statusCode, config?.shouldRemoveUnresolved ?? false, data);
  };

  /**
   * Fetches directly from the Contentful REST API and doesn't resolve entities
   * Used to avoid trying to re-serialize these entities which can have circular references.
   * @param queryOptions key/value pairs to add to the request query string
   */
  getUnresolvedEntries = async <T>(queryOptions: Record<string, string>) => {
    const queryStringOptions = new URLSearchParams(queryOptions).toString();

    const request = {
      path: `https://${this._host}/spaces/${this._space}/environments/${this._environment}/entries?${queryStringOptions}`,
    };

    return await this._fetch<ContentfulCollection<T>>(request);
  };

  getGql = async <T>(query: string, variables: any): Promise<ApiResponse<T>> => {
    return await this._fetch<T>({
      path: `https://graphql.contentful.com/content/v1/spaces/${this._space}/environments/${this._environment}`,
      method: 'POST',
      body: {
        query,
        variables,
      },
    });
  };

  getResolvedData = <T>(
    error: any,
    statusCode: number,
    removeUnresolved: boolean,
    data?: ContentfulCollection<T>
  ): ApiResponse<BasicEntryCollection<T>> => {
    if (!data) {
      return { error: error || { message: 'No data returned' }, statusCode };
    }
    if (error) {
      return { error, statusCode };
    }
    const items = resolveResponse(data, {
      removeUnresolved,
    });
    return {
      data: {
        ...data,
        items,
        total: data?.total ?? 0,
        skip: data?.skip ?? 0,
        limit: data?.limit ?? 100,
      },
      statusCode,
      error: error,
    };
  };

  getCampaigns = async (
    queryOptions: { handle: string; include?: string },
    isPagefly?: boolean
  ): Promise<ApiResponse<BasicEntryCollection<ContentfulCampaign>>> => {
    const { handle, include = '10' } = queryOptions;

    const query: Record<string, string> = isPagefly
      ? {
          content_type: 'pageCampaign',
          'fields.campaignRulesets.sys.contentType.sys.id': 'pageRuleset',
          'fields.campaignRulesets.fields.handles': handle?.split('_')[0],
          include,
        }
      : {
          content_type: 'newCampaign',
          'fields.campaignRulesets.sys.contentType.sys.id': 'campaignRulesBackup',
          'fields.campaignRulesets.fields.handles': handle?.split('_')[0],
          include,
        };

    const { data, statusCode, error } = await this.getUnresolvedEntries<ContentfulCampaign>(query);

    return this.getResolvedData<ContentfulCampaign>(error, statusCode, false, data);
  };

  getCollection = async (
    options: CollectionQuery['options']
  ): Promise<ApiResponse<BasicEntryCollection<JbContentfulCollection>>> => {
    const { data, statusCode, error } = await this.getUnresolvedEntries<JbContentfulCollection>({
      content_type: 'smartCollection',
      'fields.handle': options.handle,
      include: options?.include || '4',
    });
    return this.getResolvedData<JbContentfulCollection>(error, statusCode, false, data);
  };

  getAttachCategorySuggestions = async (options: AttachCategorySuggestionsQuery['options']) => {
    const attachCategorySuggestionsDocumentQuery = gql`
      query getAttachCategorySuggestions($purchasedCategoryId: Int!) {
        attachSuggestionCollection(limit: 3, where: { purchasedCategories: { imsCategoryId: $purchasedCategoryId } }) {
          items {
            name
            attachHeading
            productBlockHeading
            suggestedCategoriesCollection(limit: 5) {
              items {
                title
                handle
              }
            }
          }
          total
        }
      }
    `;

    const variables = {
      purchasedCategoryId: parseInt(`${options.purchasedCategoryId}`),
    };
    const resolvedData = await this.getGql<any>(
      attachCategorySuggestionsDocumentQuery.loc?.source.body || '',
      variables
    );
    return {
      ...resolvedData,
      data: {
        attachSuggestionCollection: {
          items: resolvedData.data?.data?.attachSuggestionCollection?.items || [],
        },
      },
    };
  };

  getResolvedEntries = async (
    options: Record<string, string>,
    config?: EntriesQuery['config']
  ): Promise<ApiResponse<BasicEntryCollection<any>>> => {
    return await this.getEntries<any>(options, config);
  };
}
